/**
 * HubSpot CRM v3 API Helpers (Story 25.1, AC #4, #5, #7)
 *
 * Shared API helpers for HubSpot CRM v3 endpoints.
 * Base URL: https://api.hubapi.com
 * Auth: Bearer token in Authorization header
 * Pagination: Cursor-based via `after` parameter, max limit=200 (search), 100 (list)
 *
 * Rate limits: 110 req/10s (OAuth apps per account), portal-wide daily limit.
 * Search API: 5 req/sec per portal (updated from 4 in 2024) — use sparingly.
 */

import { safeFetch } from "../safe-fetch";
import type { AdapterResult } from "../adapter";
import { adapterOk, adapterErr } from "../adapter";
export { parseHubSpotApiError } from "./errors";
import type { KVStore } from "../cache";
export type { KVStore };

const HUBSPOT_API_BASE = "https://api.hubapi.com";
export const MAX_PAGES = 20;
export const SEARCH_PAGE_SIZE = 200;

/**
 * M2: Allowed CRM object types to prevent path traversal.
 * Story 25.9 adds tickets + engagement types (calls, meetings, notes, tasks).
 * These share the same CRM v3 object API shape; the new types need explicit
 * scopes (`tickets`, etc.) at connect time. 403 on any of them cascades through
 * the same scope-missing graceful skip pattern Stories 25.6/25.7/25.8 use.
 */
const VALID_OBJECT_TYPES = new Set([
  "contacts",
  "deals",
  "companies",
  "tickets",
  "calls",
  "meetings",
  "emails",
  "notes",
  "tasks",
]);

/** R2-M4: Allowed date properties for search filter validation */
const VALID_DATE_PROPERTIES = new Set(["createdate", "closedate", "hs_lastmodifieddate"]);

export interface HubSpotPaginated<T> {
  results: T[];
  paging?: {
    next?: {
      after: string;
      link?: string;
    };
  };
}

export interface HubSpotCrmObject {
  id: string;
  properties: Record<string, string | null>;
  createdAt: string;
  updatedAt: string;
}

export interface HubSpotPipelineStage {
  id: string;
  label: string;
  displayOrder: number;
  metadata?: Record<string, string>;
}

export interface HubSpotPipeline {
  id: string;
  label: string;
  displayOrder: number;
  stages: HubSpotPipelineStage[];
}

export interface HubSpotOwner {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
}

/**
 * Build standard HubSpot API request headers.
 */
export function buildHubSpotHeaders(
  accessToken: string,
): Record<string, string> {
  return {
    Authorization: `Bearer ${accessToken}`,
    Accept: "application/json",
  };
}

/**
 * Fetch all deal pipelines with stages.
 * GET /crm/v3/pipelines/deals
 * Cached in KV for 24h.
 */
export async function fetchPipelines(
  kv: KVStore,
  accessToken: string,
  tenantId: string,
  integrationId: string,
  accountId?: string,
): Promise<AdapterResult<HubSpotPipeline[]>> {
  // M4: Include accountId in cache key to prevent stale data after re-auth with different portal
  const cacheKey = `hubspot:pipelines:${tenantId}:${integrationId}:${accountId ?? "unknown"}`;
  const cached = await kv.get(cacheKey);
  if (cached) {
    try {
      return adapterOk(JSON.parse(cached) as HubSpotPipeline[]);
    } catch {
      // R3-M2: Corrupted KV cache — fall through to fresh fetch
      await kv.delete(cacheKey);
    }
  }

  const result = await safeFetch(
    `${HUBSPOT_API_BASE}/crm/v3/pipelines/deals`,
    { headers: buildHubSpotHeaders(accessToken) },
  );

  if (!result.success) {
    return adapterErr(result.error);
  }

  const body = (await result.data.json()) as { results: HubSpotPipeline[] };
  const pipelines = body.results;

  await kv.put(cacheKey, JSON.stringify(pipelines), {
    expirationTtl: 86400,
  });

  return adapterOk(pipelines);
}

/**
 * Fetch all deal owners with ID→name mapping.
 * GET /crm/v3/owners
 * Cached in KV for 24h.
 */
export async function fetchOwners(
  kv: KVStore,
  accessToken: string,
  tenantId: string,
  integrationId: string,
  accountId?: string,
): Promise<AdapterResult<Map<string, string>>> {
  const cacheKey = `hubspot:owners:${tenantId}:${integrationId}:${accountId ?? "unknown"}`;
  const cached = await kv.get(cacheKey);
  if (cached) {
    try {
      const entries = JSON.parse(cached) as [string, string][];
      return adapterOk(new Map(entries));
    } catch {
      await kv.delete(cacheKey);
    }
  }

  const result = await safeFetch(`${HUBSPOT_API_BASE}/crm/v3/owners`, {
    headers: buildHubSpotHeaders(accessToken),
  });

  if (!result.success) {
    return adapterErr(result.error);
  }

  const body = (await result.data.json()) as { results: HubSpotOwner[] };
  const ownerMap = new Map<string, string>();
  for (const owner of body.results) {
    const name =
      [owner.firstName, owner.lastName].filter(Boolean).join(" ") ||
      owner.email;
    ownerMap.set(owner.id, name);
  }

  await kv.put(cacheKey, JSON.stringify([...ownerMap.entries()]), {
    expirationTtl: 86400,
  });

  return adapterOk(ownerMap);
}

/**
 * Fetch CRM objects with cursor pagination.
 * GET /crm/v3/objects/{objectType}?limit=100&after={cursor}&properties=...
 * MAX_PAGES guard to prevent infinite loops.
 */
export async function fetchCrmObjects(
  accessToken: string,
  objectType: string,
  properties: string[],
): Promise<AdapterResult<HubSpotCrmObject[]>> {
  // M2: Validate objectType to prevent path traversal
  if (!VALID_OBJECT_TYPES.has(objectType)) {
    return adapterErr({
      code: "invalid_response",
      message: `Invalid CRM object type: ${objectType}`,
      retryable: false,
    });
  }
  const allResults: HubSpotCrmObject[] = [];
  let after: string | undefined;
  let page = 0;

  while (page < MAX_PAGES) {
    const params = new URLSearchParams({
      limit: "100",
      properties: properties.join(","),
    });
    if (after) params.set("after", after);

    const result = await safeFetch(
      `${HUBSPOT_API_BASE}/crm/v3/objects/${objectType}?${params.toString()}`,
      { headers: buildHubSpotHeaders(accessToken) },
    );

    if (!result.success) {
      return adapterErr(result.error);
    }

    const body =
      (await result.data.json()) as HubSpotPaginated<HubSpotCrmObject>;
    allResults.push(...body.results);

    after = body.paging?.next?.after;
    if (!after) break;
    page++;
  }

  return adapterOk(allResults);
}

/**
 * Search CRM objects with date filter.
 * POST /crm/v3/objects/{objectType}/search
 *
 * WARNING: 4 req/sec limit — use sparingly.
 * HARD CAP: 10,000 results max. Portals with >10K contacts/deals in a date range
 * will get truncated results. For high-volume portals, future stories should
 * implement date-range subdivision or use the List/Scroll API as fallback.
 *
 * @see https://community.hubspot.com/t5/CRM/How-to-Fetch-More-Than-10-000-Contacts-Using-HubSpot-CRM-Search/m-p/1181820
 */
export async function searchCrmObjects(
  accessToken: string,
  objectType: string,
  properties: string[],
  dateProperty: string,
  startDate: Date,
  endDate: Date,
): Promise<AdapterResult<HubSpotCrmObject[]>> {
  // M2: Validate objectType to prevent path traversal
  if (!VALID_OBJECT_TYPES.has(objectType)) {
    return adapterErr({
      code: "invalid_response",
      message: `Invalid CRM object type: ${objectType}`,
      retryable: false,
    });
  }
  // R2-M4: Validate dateProperty
  if (!VALID_DATE_PROPERTIES.has(dateProperty)) {
    return adapterErr({
      code: "invalid_response",
      message: `Invalid date property: ${dateProperty}`,
      retryable: false,
    });
  }
  const allResults: HubSpotCrmObject[] = [];
  let after: string | undefined;
  let page = 0;

  while (page < MAX_PAGES) {
    const searchBody: Record<string, unknown> = {
      filterGroups: [
        {
          filters: [
            {
              propertyName: dateProperty,
              operator: "GTE",
              value: startDate.getTime().toString(),
            },
            {
              propertyName: dateProperty,
              operator: "LTE",
              value: endDate.getTime().toString(),
            },
          ],
        },
      ],
      properties,
      limit: SEARCH_PAGE_SIZE,
      ...(after ? { after } : {}),
    };

    const result = await safeFetch(
      `${HUBSPOT_API_BASE}/crm/v3/objects/${objectType}/search`,
      {
        method: "POST",
        headers: {
          ...buildHubSpotHeaders(accessToken),
          "Content-Type": "application/json",
        },
        body: JSON.stringify(searchBody),
      },
    );

    if (!result.success) {
      return adapterErr(result.error);
    }

    const body =
      (await result.data.json()) as HubSpotPaginated<HubSpotCrmObject>;
    allResults.push(...body.results);

    after = body.paging?.next?.after;
    if (!after) break;
    page++;
  }

  return adapterOk(allResults);
}

/**
 * Fetch contact properties for lifecycle stage discovery.
 * GET /crm/v3/properties/contacts
 */
export async function fetchContactProperties(
  kv: KVStore,
  accessToken: string,
  tenantId: string,
  integrationId: string,
  accountId?: string,
): Promise<AdapterResult<Map<string, string>>> {
  const cacheKey = `hubspot:lifecycle-stages:${tenantId}:${integrationId}:${accountId ?? "unknown"}`;
  const cached = await kv.get(cacheKey);
  if (cached) {
    try {
      const entries = JSON.parse(cached) as [string, string][];
      return adapterOk(new Map(entries));
    } catch {
      await kv.delete(cacheKey);
    }
  }

  const result = await safeFetch(
    `${HUBSPOT_API_BASE}/crm/v3/properties/contacts`,
    { headers: buildHubSpotHeaders(accessToken) },
  );

  if (!result.success) {
    return adapterErr(result.error);
  }

  const body = (await result.data.json()) as {
    results: Array<{
      name: string;
      options?: Array<{ value: string; label: string }>;
    }>;
  };

  const lifecycleProp = body.results.find((p) => p.name === "lifecyclestage");
  const stageMap = new Map<string, string>();

  if (lifecycleProp?.options) {
    for (const opt of lifecycleProp.options) {
      stageMap.set(opt.value, opt.label);
    }
  }

  await kv.put(cacheKey, JSON.stringify([...stageMap.entries()]), {
    expirationTtl: 86400,
  });

  return adapterOk(stageMap);
}

/**
 * Story 25.6: Result of an email API call that may be gracefully skipped
 * when the `content` scope is missing. null = scope missing (403); otherwise data.
 */
export type EmailStatsScopeResult<T> =
  | { readonly kind: "ok"; readonly data: T }
  | { readonly kind: "scope_missing" };

/**
 * Email Statistics API aggregate response shape.
 * Only 16 verified counters and 12 verified ratios are typed; vendor response
 * may contain more but we ignore them per Story 25.6 AC #2/#3.
 */
export interface EmailStatsAggregate {
  readonly counters?: Record<string, number | null>;
  readonly ratios?: Record<string, number | null>;
  readonly deviceBreakdown?: {
    readonly open_device_type?: Record<string, number | null>;
    // click_device_type intentionally omitted — adapter does not surface click-device metrics yet
  };
}

export interface EmailStatsResponse {
  readonly aggregate?: EmailStatsAggregate;
}

/**
 * Story 25.6: Fetch Marketing Email aggregate statistics.
 * GET /marketing/v3/emails/statistics/list?startTimestamp=...&endTimestamp=...
 *
 * Requires `content` scope (SuperAdmin only). Returns `scope_missing` on 403
 * so callers can degrade gracefully to zero records without breaking CRM metrics.
 *
 * @see https://developers.hubspot.com/docs/api/marketing/marketing-email
 */
export async function fetchEmailStatistics(
  accessToken: string,
  startDate: Date,
  endDate: Date,
): Promise<AdapterResult<EmailStatsScopeResult<EmailStatsResponse>>> {
  const params = new URLSearchParams({
    startTimestamp: startDate.getTime().toString(),
    endTimestamp: endDate.getTime().toString(),
  });

  const result = await safeFetch(
    `${HUBSPOT_API_BASE}/marketing/v3/emails/statistics/list?${params.toString()}`,
    { headers: buildHubSpotHeaders(accessToken) },
  );

  if (!result.success) {
    // 403 = missing `content` scope: treat as graceful skip, not an error
    if (result.error.providerStatus === 403) {
      return adapterOk({ kind: "scope_missing" as const });
    }
    return adapterErr(result.error);
  }

  try {
    const data = (await result.data.json()) as EmailStatsResponse;
    return adapterOk({ kind: "ok" as const, data });
  } catch {
    return adapterErr({
      code: "invalid_response",
      message: "HubSpot email statistics returned invalid JSON",
      retryable: false,
    });
  }
}

/**
 * Story 25.6: Marketing Email object (from /marketing/v3/emails).
 * Fields used by our adapter for emailCampaigns reportType.
 */
export interface MarketingEmailObject {
  readonly id: string;
  readonly name?: string | null;
  readonly subject?: string | null;
  readonly type?: string | null;
  readonly state?: string | null;
  readonly publishDate?: string | null;
  readonly created?: string | null;
  readonly stats?: {
    readonly counters?: Record<string, number | null>;
    readonly ratios?: Record<string, number | null>;
  } | null;
}

/**
 * Story 25.6: Fetch Marketing Email objects with their per-email stats.
 * GET /marketing/v3/emails?limit=...&includeStats=true
 *
 * Requires `content` scope. Cursor-paginated via `after`. MAX_PAGES guard.
 * Returns `truncated: true` when MAX_PAGES limit was hit and more pages remained,
 * so callers can flag partial data to downstream widgets.
 */
export async function fetchMarketingEmails(
  accessToken: string,
  limit: number = 100,
): Promise<AdapterResult<EmailStatsScopeResult<{ emails: MarketingEmailObject[]; truncated: boolean }>>> {
  const allResults: MarketingEmailObject[] = [];
  let after: string | undefined;
  let page = 0;
  let truncated = false;
  // Cap limit to HubSpot's 100 max per page
  const pageLimit = Math.min(Math.max(1, limit), 100);

  while (page < MAX_PAGES) {
    const params = new URLSearchParams({
      limit: pageLimit.toString(),
      includeStats: "true",
    });
    if (after) params.set("after", after);

    const result = await safeFetch(
      `${HUBSPOT_API_BASE}/marketing/v3/emails?${params.toString()}`,
      { headers: buildHubSpotHeaders(accessToken) },
    );

    if (!result.success) {
      if (result.error.providerStatus === 403) {
        return adapterOk({ kind: "scope_missing" as const });
      }
      return adapterErr(result.error);
    }

    let body: HubSpotPaginated<MarketingEmailObject>;
    try {
      body = (await result.data.json()) as HubSpotPaginated<MarketingEmailObject>;
    } catch {
      return adapterErr({
        code: "invalid_response",
        message: "HubSpot marketing emails returned invalid JSON",
        retryable: false,
      });
    }

    allResults.push(...body.results);

    after = body.paging?.next?.after;
    if (!after) break;
    page++;
    // M3: Flag truncation when we hit MAX_PAGES but more pages remained
    if (page >= MAX_PAGES && after) {
      truncated = true;
    }
  }

  return adapterOk({ kind: "ok" as const, data: { emails: allResults, truncated } });
}

/**
 * Story 25.6: Email Event from legacy Email Events API v1.
 */
export interface EmailEventV1 {
  readonly id?: string;
  readonly created: number; // epoch ms
  readonly type: string; // SENT, DELIVERED, OPEN, CLICK, BOUNCE, SPAMREPORT, UNSUBSCRIBE, DEFERRED
  readonly recipient?: string;
  readonly emailCampaignId?: number;
}

/** Allowed email event types (AC #6). */
export const EMAIL_EVENT_TYPES = [
  "SENT",
  "DELIVERED",
  "OPEN",
  "CLICK",
  "BOUNCE",
  "SPAMREPORT",
  "UNSUBSCRIBE",
  "DEFERRED",
] as const;

export type EmailEventType = typeof EMAIL_EVENT_TYPES[number];

/**
 * Story 25.6: Fetch email events from legacy Email Events API v1.
 * GET /email/public/v1/events?eventType=X&startTimestamp=Y&endTimestamp=Z
 *
 * Requires `content` scope. Legacy v1 API — remains active as of Apr 2026 per HubSpot changelog.
 *
 * **Pagination note:** v1 endpoint uses a CURSOR TOKEN confusingly named `offset`
 * (different from the `after` cursor used by v3 CRM endpoints). The server returns
 * `hasMore: bool` and an opaque `offset: string` token that must be round-tripped as-is.
 * MAX_PAGES * 1000 events/page = up to 20K events per event type.
 *
 * **Date window:** HubSpot v1 returns events from at most the LAST 30 DAYS. Callers
 * requesting older date ranges should clamp startDate to now-30d to avoid silent
 * truncation.
 */
export async function fetchEmailEvents(
  accessToken: string,
  eventType: EmailEventType,
  startDate: Date,
  endDate: Date,
): Promise<AdapterResult<EmailStatsScopeResult<EmailEventV1[]>>> {
  // Type guarantee: eventType is already EmailEventType (compile-time allowlist).

  const allResults: EmailEventV1[] = [];
  let offset: string | undefined;
  let page = 0;

  while (page < MAX_PAGES) {
    const params = new URLSearchParams({
      eventType,
      startTimestamp: startDate.getTime().toString(),
      endTimestamp: endDate.getTime().toString(),
      limit: "1000",
    });
    if (offset) params.set("offset", offset);

    const result = await safeFetch(
      `${HUBSPOT_API_BASE}/email/public/v1/events?${params.toString()}`,
      { headers: buildHubSpotHeaders(accessToken) },
    );

    if (!result.success) {
      if (result.error.providerStatus === 403) {
        return adapterOk({ kind: "scope_missing" as const });
      }
      return adapterErr(result.error);
    }

    let body: { events: EmailEventV1[]; hasMore?: boolean; offset?: string };
    try {
      body = (await result.data.json()) as typeof body;
    } catch {
      return adapterErr({
        code: "invalid_response",
        message: "HubSpot email events returned invalid JSON",
        retryable: false,
      });
    }

    if (Array.isArray(body.events)) {
      allResults.push(...body.events);
    }

    if (!body.hasMore || !body.offset) break;
    offset = body.offset;
    page++;
  }

  return adapterOk({ kind: "ok" as const, data: allResults });
}

/**
 * Fetch deal properties for custom field discovery.
 * GET /crm/v3/properties/deals
 */
export async function fetchDealProperties(
  accessToken: string,
): Promise<
  AdapterResult<
    Array<{ name: string; label: string; type: string; options?: Array<{ value: string; label: string }> }>
  >
> {
  const result = await safeFetch(
    `${HUBSPOT_API_BASE}/crm/v3/properties/deals`,
    { headers: buildHubSpotHeaders(accessToken) },
  );

  if (!result.success) {
    return adapterErr(result.error);
  }

  const body = (await result.data.json()) as {
    results: Array<{
      name: string;
      label: string;
      type: string;
      options?: Array<{ value: string; label: string }>;
    }>;
  };

  return adapterOk(body.results);
}

// ============================================================================
// Story 25.7: Traffic Analytics (Analytics v2 API)
// ============================================================================

/**
 * Story 25.7: Analytics v2 uses a legacy YYYYMMDDHH date format (not ISO 8601).
 * HubSpot's docs accept YYYYMMDD as a shorthand that defaults hour to 00. We
 * emit the explicit YYYYMMDDHH form so truncated hours never surprise us.
 */
export function formatAnalyticsV2Date(date: Date, hour: "00" | "23" = "00"): string {
  const y = date.getUTCFullYear().toString().padStart(4, "0");
  const m = (date.getUTCMonth() + 1).toString().padStart(2, "0");
  const d = date.getUTCDate().toString().padStart(2, "0");
  return `${y}${m}${d}${hour}`;
}

/**
 * Story 25.7: Raw totals returned by /analytics/v2/reports/totals/total.
 *
 * The vendor response also includes leads/contacts/customers/subscribers but we
 * intentionally ignore those — they duplicate CRM search metrics and can disagree
 * for reasons the adapter can't explain.
 */
export interface TrafficTotalsResponse {
  readonly totals?: {
    readonly visits?: number | null;
    readonly visitors?: number | null;
    readonly pageviews?: number | null;
    readonly pagesPerVisit?: number | null;
    readonly timePerVisit?: number | null;
    readonly bounceRate?: number | null;
    readonly newVisits?: number | null;
    readonly returningVisits?: number | null;
  };
}

/**
 * Story 25.7: Sources breakdown — keyed by source enum (ORGANIC_SEARCH, DIRECT_TRAFFIC, etc).
 * Each value may be an array of daily rows (when time_period=daily) or a single
 * totals object (when time_period=total). We accept both shapes to tolerate
 * portal variance.
 */
export interface TrafficSourcesResponse {
  readonly breakdowns?: Record<
    string,
    | ReadonlyArray<{
        readonly visits?: number | null;
        readonly visitors?: number | null;
        readonly pageviews?: number | null;
        readonly bounceRate?: number | null;
      }>
    | {
        readonly visits?: number | null;
        readonly visitors?: number | null;
        readonly pageviews?: number | null;
        readonly bounceRate?: number | null;
      }
  >;
}

/**
 * Story 25.7: Daily sessions response — keyed by YYYYMMDD date string.
 * Each value is a single row of daily totals for that date.
 */
export interface TrafficSessionsDailyResponse {
  readonly [dateKey: string]:
    | {
        readonly visits?: number | null;
        readonly visitors?: number | null;
        readonly pageviews?: number | null;
        readonly bounceRate?: number | null;
      }
    | undefined;
}

/**
 * Story 25.7: Fetch aggregate traffic totals.
 * GET /analytics/v2/reports/totals/total?start={YYYYMMDDHH}&end={YYYYMMDDHH}
 *
 * Requires `content` scope. Returns `scope_missing` on 403 (same graceful-skip
 * pattern as Story 25.6 email stats).
 */
export async function fetchTrafficTotals(
  accessToken: string,
  startDate: Date,
  endDate: Date,
): Promise<AdapterResult<EmailStatsScopeResult<TrafficTotalsResponse>>> {
  const params = new URLSearchParams({
    start: formatAnalyticsV2Date(startDate, "00"),
    end: formatAnalyticsV2Date(endDate, "23"),
  });

  const result = await safeFetch(
    `${HUBSPOT_API_BASE}/analytics/v2/reports/totals/total?${params.toString()}`,
    { headers: buildHubSpotHeaders(accessToken) },
  );

  if (!result.success) {
    if (result.error.providerStatus === 403) {
      return adapterOk({ kind: "scope_missing" as const });
    }
    return adapterErr(result.error);
  }

  try {
    const data = (await result.data.json()) as TrafficTotalsResponse;
    return adapterOk({ kind: "ok" as const, data });
  } catch {
    return adapterErr({
      code: "invalid_response",
      message: "HubSpot traffic totals returned invalid JSON",
      retryable: false,
    });
  }
}

/**
 * Story 25.7: Fetch sources breakdown (daily granularity, aggregated in adapter).
 * GET /analytics/v2/reports/sources/daily?start=...&end=...
 */
export async function fetchTrafficSources(
  accessToken: string,
  startDate: Date,
  endDate: Date,
): Promise<AdapterResult<EmailStatsScopeResult<TrafficSourcesResponse>>> {
  const params = new URLSearchParams({
    start: formatAnalyticsV2Date(startDate, "00"),
    end: formatAnalyticsV2Date(endDate, "23"),
  });

  const result = await safeFetch(
    `${HUBSPOT_API_BASE}/analytics/v2/reports/sources/daily?${params.toString()}`,
    { headers: buildHubSpotHeaders(accessToken) },
  );

  if (!result.success) {
    if (result.error.providerStatus === 403) {
      return adapterOk({ kind: "scope_missing" as const });
    }
    return adapterErr(result.error);
  }

  try {
    const data = (await result.data.json()) as TrafficSourcesResponse;
    return adapterOk({ kind: "ok" as const, data });
  } catch {
    return adapterErr({
      code: "invalid_response",
      message: "HubSpot traffic sources returned invalid JSON",
      retryable: false,
    });
  }
}

/**
 * Story 25.7: Fetch daily sessions breakdown.
 * GET /analytics/v2/reports/sessions/daily?start=...&end=...
 */
export async function fetchTrafficSessionsDaily(
  accessToken: string,
  startDate: Date,
  endDate: Date,
): Promise<AdapterResult<EmailStatsScopeResult<TrafficSessionsDailyResponse>>> {
  const params = new URLSearchParams({
    start: formatAnalyticsV2Date(startDate, "00"),
    end: formatAnalyticsV2Date(endDate, "23"),
  });

  const result = await safeFetch(
    `${HUBSPOT_API_BASE}/analytics/v2/reports/sessions/daily?${params.toString()}`,
    { headers: buildHubSpotHeaders(accessToken) },
  );

  if (!result.success) {
    if (result.error.providerStatus === 403) {
      return adapterOk({ kind: "scope_missing" as const });
    }
    return adapterErr(result.error);
  }

  try {
    const data = (await result.data.json()) as TrafficSessionsDailyResponse;
    return adapterOk({ kind: "ok" as const, data });
  } catch {
    return adapterErr({
      code: "invalid_response",
      message: "HubSpot traffic sessions returned invalid JSON",
      retryable: false,
    });
  }
}

// ============================================================================
// Story 25.8: Marketing Campaigns API (/marketing/v3/campaigns)
// ============================================================================

/** Maximum number of campaigns the adapter processes per fetch cycle. Above this
 * threshold we bail out rather than burn the 100 req/10s budget. 50 campaigns =
 * 1 list call + 100 report calls (metrics + revenue) = 101 total calls. */
export const MAX_CAMPAIGNS = 50;

/** Concurrent request batch size for per-campaign report fetches. HubSpot's
 * published OAuth burst limit is 100 req/10s; 5 parallel requests leaves
 * headroom for other concurrent adapter fetches in the same cycle. */
export const CAMPAIGN_BATCH_SIZE = 5;

/** Delay between batches (ms). 5 calls / 1.2s = ~4.2 rps, well under the
 * documented 10 rps steady-state. */
export const CAMPAIGN_BATCH_DELAY_MS = 1200;

/**
 * Story 25.8: Campaign object returned by /marketing/v3/campaigns list endpoint.
 *
 * HubSpot's v3 Campaigns API returns each campaign as `{id, properties, ...}`
 * with the campaign name under `properties.hs_name`. Owner and type are
 * optional — portals without the feature enabled omit them entirely.
 */
export interface HubSpotCampaign {
  readonly id: string;
  readonly properties?: {
    readonly hs_name?: string | null;
    readonly hs_owner?: string | null;
    readonly hs_type?: string | null;
  } | null;
}

/**
 * Story 25.8: Fetch list of marketing campaigns with cursor pagination.
 * GET /marketing/v3/campaigns?limit=100&after={cursor}&properties=hs_name,hs_owner,hs_type
 *
 * Requires `content` scope (SuperAdmin only). Returns `scope_missing` on 403.
 * Hard-caps total campaigns at MAX_CAMPAIGNS to stay within rate limits when
 * followed by 2 per-campaign report calls.
 *
 * @see https://developers.hubspot.com/docs/api-reference/marketing-campaigns-public-api-v3
 */
export async function fetchCampaigns(
  accessToken: string,
): Promise<AdapterResult<EmailStatsScopeResult<{ campaigns: HubSpotCampaign[]; truncated: boolean }>>> {
  const allResults: HubSpotCampaign[] = [];
  let after: string | undefined;
  // R1-H2: With MAX_CAMPAIGNS=50 and HubSpot's 100-per-page limit, a portal
  // with ≥50 campaigns will exit this loop on page 0 (hitting the cap).
  // Portals with <50 campaigns exit on page 0 when `paging.next.after` is
  // absent. The MAX_PAGES guard is a belt-and-suspenders safeguard in case
  // a future API version reduces the per-page limit below MAX_CAMPAIGNS.
  for (let page = 0; page < MAX_PAGES; page++) {
    const params = new URLSearchParams({
      limit: "100",
      // R3-L3: Request only the fields the adapter consumes. hs_campaign_status,
      // hs_start_date, hs_end_date were requested previously but never rendered.
      properties: "hs_name,hs_owner,hs_type",
    });
    if (after) params.set("after", after);

    const result = await safeFetch(
      `${HUBSPOT_API_BASE}/marketing/v3/campaigns?${params.toString()}`,
      { headers: buildHubSpotHeaders(accessToken) },
    );

    if (!result.success) {
      if (result.error.providerStatus === 403) {
        return adapterOk({ kind: "scope_missing" as const });
      }
      return adapterErr(result.error);
    }

    let body: HubSpotPaginated<HubSpotCampaign>;
    try {
      body = (await result.data.json()) as HubSpotPaginated<HubSpotCampaign>;
    } catch {
      return adapterErr({
        code: "invalid_response",
        message: "HubSpot campaigns returned invalid JSON",
        retryable: false,
      });
    }

    allResults.push(...body.results);
    const nextCursor = body.paging?.next?.after;

    // Cap at MAX_CAMPAIGNS to protect the subsequent per-campaign report budget.
    if (allResults.length >= MAX_CAMPAIGNS) {
      const truncated = allResults.length > MAX_CAMPAIGNS || Boolean(nextCursor);
      return adapterOk({
        kind: "ok" as const,
        data: { campaigns: allResults.slice(0, MAX_CAMPAIGNS), truncated },
      });
    }

    if (!nextCursor) {
      return adapterOk({ kind: "ok" as const, data: { campaigns: allResults, truncated: false } });
    }
    after = nextCursor;
  }

  // Hit MAX_PAGES with more pages remaining — surface as truncated.
  return adapterOk({ kind: "ok" as const, data: { campaigns: allResults, truncated: true } });
}

/**
 * Story 25.8: Engagement metrics returned by /reports/metrics (4 fields only).
 * Revenue fields live on /reports/revenue — see CampaignRevenueReport below.
 */
export interface CampaignMetricsReport {
  readonly sessions?: number | null;
  readonly newContactsFirstTouch?: number | null;
  readonly newContactsLastTouch?: number | null;
  readonly influencedContacts?: number | null;
}

/**
 * Story 25.8: Revenue/attribution metrics returned by /reports/revenue.
 * Separate from CampaignMetricsReport — HubSpot split these endpoints
 * intentionally; initial research that assumed they were one call was wrong.
 */
export interface CampaignRevenueReport {
  readonly contactsNumber?: number | null;
  readonly currencyCode?: string | null;
  readonly dealAmount?: number | null;
  readonly dealsNumber?: number | null;
  readonly revenueAmount?: number | null;
}

/** GUID allow-list regex — HubSpot campaign GUIDs are UUIDv4. Applied before
 * interpolating into the URL path to block path traversal / injection. */
const CAMPAIGN_GUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * R1-H1: Returns an error Result that's typed to be assignable to any caller's
 * success type. `AdapterResult<never>` can only ever be the error variant —
 * the success branch is impossible by construction — so it safely widens to
 * `AdapterResult<EmailStatsScopeResult<T>>` without the unsound cast.
 */
function validateCampaignGuid(guid: string): AdapterResult<never> | null {
  if (!CAMPAIGN_GUID_REGEX.test(guid)) {
    return adapterErr({
      code: "invalid_response",
      message: "Invalid HubSpot campaign GUID format",
      retryable: false,
    });
  }
  return null;
}

/**
 * Story 25.8: Fetch engagement metrics for a single campaign.
 * GET /marketing/v3/campaigns/{campaignGuid}/reports/metrics?startDate=&endDate=
 *
 * Returns exactly 4 fields: sessions, newContactsFirstTouch,
 * newContactsLastTouch, influencedContacts. Revenue fields are on a SEPARATE
 * endpoint — do NOT expect them here.
 */
export async function fetchCampaignMetrics(
  accessToken: string,
  campaignGuid: string,
  startDate: Date,
  endDate: Date,
): Promise<AdapterResult<EmailStatsScopeResult<CampaignMetricsReport>>> {
  const guardErr = validateCampaignGuid(campaignGuid);
  if (guardErr) return guardErr;

  const params = new URLSearchParams({
    startDate: startDate.toISOString().slice(0, 10),
    endDate: endDate.toISOString().slice(0, 10),
  });

  const result = await safeFetch(
    `${HUBSPOT_API_BASE}/marketing/v3/campaigns/${campaignGuid}/reports/metrics?${params.toString()}`,
    { headers: buildHubSpotHeaders(accessToken) },
  );

  if (!result.success) {
    if (result.error.providerStatus === 403) {
      return adapterOk({ kind: "scope_missing" as const });
    }
    // 404 = campaign deleted between list and fetch; treat as empty, not fatal
    if (result.error.providerStatus === 404) {
      return adapterOk({ kind: "ok" as const, data: {} });
    }
    return adapterErr(result.error);
  }

  try {
    const data = (await result.data.json()) as CampaignMetricsReport;
    return adapterOk({ kind: "ok" as const, data });
  } catch {
    return adapterErr({
      code: "invalid_response",
      message: "HubSpot campaign metrics returned invalid JSON",
      retryable: false,
    });
  }
}

/**
 * Story 25.8: Fetch revenue/attribution metrics for a single campaign.
 * GET /marketing/v3/campaigns/{campaignGuid}/reports/revenue?startDate=&endDate=
 *
 * Returns contactsNumber, currencyCode, dealAmount, dealsNumber, revenueAmount.
 * Requires scope `marketing.campaigns.revenue.read` (included under `content`).
 */
export async function fetchCampaignRevenue(
  accessToken: string,
  campaignGuid: string,
  startDate: Date,
  endDate: Date,
): Promise<AdapterResult<EmailStatsScopeResult<CampaignRevenueReport>>> {
  const guardErr = validateCampaignGuid(campaignGuid);
  if (guardErr) return guardErr;

  const params = new URLSearchParams({
    startDate: startDate.toISOString().slice(0, 10),
    endDate: endDate.toISOString().slice(0, 10),
  });

  const result = await safeFetch(
    `${HUBSPOT_API_BASE}/marketing/v3/campaigns/${campaignGuid}/reports/revenue?${params.toString()}`,
    { headers: buildHubSpotHeaders(accessToken) },
  );

  if (!result.success) {
    if (result.error.providerStatus === 403) {
      return adapterOk({ kind: "scope_missing" as const });
    }
    if (result.error.providerStatus === 404) {
      return adapterOk({ kind: "ok" as const, data: {} });
    }
    return adapterErr(result.error);
  }

  try {
    const data = (await result.data.json()) as CampaignRevenueReport;
    return adapterOk({ kind: "ok" as const, data });
  } catch {
    return adapterErr({
      code: "invalid_response",
      message: "HubSpot campaign revenue returned invalid JSON",
      retryable: false,
    });
  }
}

// ============================================================================
// Story 25.9: Companies, Tickets & Engagements (CRM v3 objects)
//
// Companies use existing `crm.objects.companies.read` scope (granted pre-25.9).
// Tickets use the new `tickets` scope added in Story 25.9. Pre-25.9 connections
// will 403 on ticket + engagement endpoints — we return `scope_missing` so the
// adapter can flag the metrics as unavailable instead of failing the whole
// fetch. Engagement objects (calls/meetings/notes/tasks) are included under
// the `tickets` scope per HubSpot's grouping.
// ============================================================================

/** Story 25.9: Result shape for scope-aware fetch/search wrappers. */
export interface ScopedCrmObjectsResult {
  readonly objects: HubSpotCrmObject[];
  /** True when pagination hit MAX_PAGES with more pages remaining (AC #1/#2 honesty). */
  readonly truncated: boolean;
}

/**
 * Fetch CRM objects with scope-aware 403 handling + truncation flag.
 * Returns `scope_missing` on 403 instead of adapterErr so callers can show a
 * "reconnect" CTA rather than breaking the entire fetch cycle.
 *
 * We re-implement instead of wrapping `fetchCrmObjects` because the legacy
 * helper throws on 403 (hard error). We also surface `truncated` when a
 * portal has more than MAX_PAGES*100 objects of this type — consumers can
 * then display a "showing first N" disclaimer.
 */
export async function fetchCrmObjectsWithScope(
  accessToken: string,
  objectType: string,
  properties: string[],
): Promise<AdapterResult<EmailStatsScopeResult<ScopedCrmObjectsResult>>> {
  if (!VALID_OBJECT_TYPES.has(objectType)) {
    return adapterErr({
      code: "invalid_response",
      message: `Invalid CRM object type: ${objectType}`,
      retryable: false,
    });
  }
  const allResults: HubSpotCrmObject[] = [];
  let after: string | undefined;
  let page = 0;
  let truncated = false;

  while (page < MAX_PAGES) {
    const params = new URLSearchParams({
      limit: "100",
      properties: properties.join(","),
    });
    if (after) params.set("after", after);

    const result = await safeFetch(
      `${HUBSPOT_API_BASE}/crm/v3/objects/${objectType}?${params.toString()}`,
      { headers: buildHubSpotHeaders(accessToken) },
    );

    if (!result.success) {
      if (result.error.providerStatus === 403) {
        return adapterOk({ kind: "scope_missing" as const });
      }
      return adapterErr(result.error);
    }

    let body: HubSpotPaginated<HubSpotCrmObject>;
    try {
      body = (await result.data.json()) as HubSpotPaginated<HubSpotCrmObject>;
    } catch {
      return adapterErr({
        code: "invalid_response",
        message: `HubSpot ${objectType} list returned invalid JSON`,
        retryable: false,
      });
    }

    allResults.push(...body.results);
    after = body.paging?.next?.after;
    if (!after) break;
    page++;
    if (page >= MAX_PAGES && after) {
      truncated = true;
    }
  }

  return adapterOk({ kind: "ok" as const, data: { objects: allResults, truncated } });
}

/**
 * Search CRM objects by date range with scope-aware 403 handling + truncation flag.
 * Mirrors `searchCrmObjects` but converts 403 into `scope_missing` (Story 25.9).
 */
export async function searchCrmObjectsWithScope(
  accessToken: string,
  objectType: string,
  properties: string[],
  dateProperty: string,
  startDate: Date,
  endDate: Date,
): Promise<AdapterResult<EmailStatsScopeResult<ScopedCrmObjectsResult>>> {
  if (!VALID_OBJECT_TYPES.has(objectType)) {
    return adapterErr({
      code: "invalid_response",
      message: `Invalid CRM object type: ${objectType}`,
      retryable: false,
    });
  }
  if (!VALID_DATE_PROPERTIES.has(dateProperty)) {
    return adapterErr({
      code: "invalid_response",
      message: `Invalid date property: ${dateProperty}`,
      retryable: false,
    });
  }

  const allResults: HubSpotCrmObject[] = [];
  let after: string | undefined;
  let page = 0;
  let truncated = false;

  while (page < MAX_PAGES) {
    const searchBody: Record<string, unknown> = {
      filterGroups: [
        {
          filters: [
            {
              propertyName: dateProperty,
              operator: "GTE",
              value: startDate.getTime().toString(),
            },
            {
              propertyName: dateProperty,
              operator: "LTE",
              value: endDate.getTime().toString(),
            },
          ],
        },
      ],
      properties,
      limit: SEARCH_PAGE_SIZE,
      ...(after ? { after } : {}),
    };

    const result = await safeFetch(
      `${HUBSPOT_API_BASE}/crm/v3/objects/${objectType}/search`,
      {
        method: "POST",
        headers: {
          ...buildHubSpotHeaders(accessToken),
          "Content-Type": "application/json",
        },
        body: JSON.stringify(searchBody),
      },
    );

    if (!result.success) {
      if (result.error.providerStatus === 403) {
        return adapterOk({ kind: "scope_missing" as const });
      }
      return adapterErr(result.error);
    }

    let body: HubSpotPaginated<HubSpotCrmObject>;
    try {
      body = (await result.data.json()) as HubSpotPaginated<HubSpotCrmObject>;
    } catch {
      return adapterErr({
        code: "invalid_response",
        message: `HubSpot ${objectType} search returned invalid JSON`,
        retryable: false,
      });
    }

    allResults.push(...body.results);
    after = body.paging?.next?.after;
    if (!after) break;
    page++;
    if (page >= MAX_PAGES && after) {
      truncated = true;
    }
  }

  return adapterOk({ kind: "ok" as const, data: { objects: allResults, truncated } });
}

// ============================================================================
// Story 25.10: Forms (Marketing Forms v3 + Form Integrations v1) + Marketing Events
//
// Forms: two-step fetch. `/marketing/v3/forms` lists form metadata; we then call
// `/form-integrations/v1/submissions/forms/{formGuid}` per form and paginate to
// count submissions (v1 endpoint exposes individual submissions, NOT a top-level
// `total` counter — counting by pagination is the only option).
//
// Marketing Events: `/marketing/v3/marketing-events` returns participation
// counters (`registrants`, `attendees`, `cancellations`, `noShows`) embedded
// on each event record, so NO second call per event is required.
//
// Both endpoint groups require optional OAuth scopes (`forms`,
// `crm.objects.marketing_events.read`) that may be absent on older connections;
// we translate 403 → `scope_missing` so the adapter degrades gracefully.
// ============================================================================

/** Forms/Events scope-aware result (same shape as EmailStatsScopeResult for reuse). */
export type FormsEventsScopeResult<T> = EmailStatsScopeResult<T>;

/** Maximum forms iterated per fetch cycle. 1 list call + 50 forms × ≤8 pages =
 * up to 401 calls worst case; typical 50-form portals settle near 51 calls. */
export const MAX_FORMS = 50;

/** Maximum pagination pages per form when counting submissions. 50 per page ×
 * 8 pages = up to 400 counted submissions per form before truncation. */
export const MAX_FORM_SUBMISSION_PAGES = 8;

/** Cap on marketing events iterated per fetch cycle. Counters are embedded on
 * the list response so no per-event calls are needed — the cap guards memory. */
export const MAX_MARKETING_EVENTS = 200;

/** Page size for /marketing/v3/forms. HubSpot hard-caps at 100. */
const FORMS_PAGE_SIZE = 100;

/** Page size for /form-integrations/v1/submissions/forms/{guid}. Vendor max is 50. */
const FORM_SUBMISSIONS_PAGE_SIZE = 50;

/** Page size for /marketing/v3/marketing-events. HubSpot hard-caps at 100. */
const MARKETING_EVENTS_PAGE_SIZE = 100;

/** Concurrency batch for per-form submission count calls. Mirrors campaign
 * batching so Stories 25.8 + 25.10 share the same rate-limit pacing pattern.
 * Exported so tests can stub `setTimeout` by zeroing the delay. */
export const FORM_SUBMISSION_BATCH_SIZE = 5;

/** Delay between form-submission batches (ms). 5 calls / 1.2s = ~4.2 rps;
 * keeps plenty of headroom under HubSpot's 100 req/10s OAuth cap.
 * Tests override this constant to 0 via the module mock. */
export const FORM_SUBMISSION_BATCH_DELAY_MS = 1200;

/** Form GUID regex — Marketing Forms v3 guids are UUIDv4. Blocks path traversal
 * when interpolating into `/form-integrations/v1/submissions/forms/{formGuid}`. */
const FORM_GUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Story 25.10: Form object returned by /marketing/v3/forms list endpoint.
 * HubSpot v3 returns form metadata with `id` (the GUID) + `name`; `formType`,
 * `createdAt`, `updatedAt`, and `archived` are optional.
 */
export interface HubSpotForm {
  readonly id: string;
  readonly name?: string | null;
  readonly fieldGroups?: unknown;
  readonly createdAt?: string | null;
  readonly updatedAt?: string | null;
  readonly formType?: string | null;
  readonly archived?: boolean | null;
}

/**
 * Story 25.10: Fetch list of Marketing Forms v3 with cursor pagination.
 * GET /marketing/v3/forms?limit=100&after={cursor}
 *
 * Requires `forms` optional scope. Returns `scope_missing` on 403.
 * Caps total forms at MAX_FORMS to stay within rate limits when followed
 * by per-form submission count calls.
 */
export async function fetchForms(
  accessToken: string,
): Promise<AdapterResult<FormsEventsScopeResult<{ forms: HubSpotForm[]; truncated: boolean }>>> {
  const allResults: HubSpotForm[] = [];
  let after: string | undefined;

  for (let page = 0; page < MAX_PAGES; page++) {
    const params = new URLSearchParams({ limit: FORMS_PAGE_SIZE.toString() });
    if (after) params.set("after", after);

    const result = await safeFetch(
      `${HUBSPOT_API_BASE}/marketing/v3/forms?${params.toString()}`,
      { headers: buildHubSpotHeaders(accessToken) },
    );

    if (!result.success) {
      if (result.error.providerStatus === 403) {
        return adapterOk({ kind: "scope_missing" as const });
      }
      return adapterErr(result.error);
    }

    let body: HubSpotPaginated<HubSpotForm>;
    try {
      body = (await result.data.json()) as HubSpotPaginated<HubSpotForm>;
    } catch {
      return adapterErr({
        code: "invalid_response",
        message: "HubSpot forms list returned invalid JSON",
        retryable: false,
      });
    }

    const results = Array.isArray(body.results) ? body.results : [];
    allResults.push(...results);
    const nextCursor = body.paging?.next?.after;

    // Hard-cap at MAX_FORMS to protect the per-form submission call budget
    if (allResults.length >= MAX_FORMS) {
      const truncated = allResults.length > MAX_FORMS || Boolean(nextCursor);
      return adapterOk({
        kind: "ok" as const,
        data: { forms: allResults.slice(0, MAX_FORMS), truncated },
      });
    }

    if (!nextCursor) {
      return adapterOk({ kind: "ok" as const, data: { forms: allResults, truncated: false } });
    }
    after = nextCursor;
  }

  // Hit MAX_PAGES — surface truncation honestly
  return adapterOk({ kind: "ok" as const, data: { forms: allResults, truncated: true } });
}

/**
 * Story 25.10: Count submissions for a single form.
 *
 * GET /form-integrations/v1/submissions/forms/{formGuid}?limit=50&after={cursor}
 *
 * v1 legacy endpoint returns individual submissions; no `total` counter is
 * available in the response, so counting requires paginating. We cap pagination
 * at MAX_FORM_SUBMISSION_PAGES so one hot form can't consume the whole rate-limit
 * budget. `truncated=true` means the returned count is a floor, not the true total.
 *
 * Returns `scope_missing` on 403 (portal revoked `forms` optional scope).
 * 404 is treated as zero (form deleted between list and fetch).
 *
 * **Mid-loop 403 behavior (R1-M2):** If page 1 succeeds (e.g., 50 submissions
 * counted) and page 2 returns 403, we return `scope_missing` and discard the
 * partial count. In practice scope revocations happen between fetches, not
 * mid-paginate — this branch is defensive. A follow-up could surface a partial
 * count by returning `{ kind: "ok", data: { count, truncated: true } }` with a
 * scope-drop flag, but for now atomic discard keeps the summary honest: either
 * the portal has scope (full count) or it doesn't (zero, flagged unavailable).
 */
export async function fetchFormSubmissionsCount(
  accessToken: string,
  formGuid: string,
): Promise<AdapterResult<FormsEventsScopeResult<{ count: number; truncated: boolean }>>> {
  if (!FORM_GUID_REGEX.test(formGuid)) {
    return adapterErr({
      code: "invalid_response",
      message: "Invalid HubSpot form GUID format",
      retryable: false,
    });
  }

  let count = 0;
  let after: string | undefined;

  for (let page = 0; page < MAX_FORM_SUBMISSION_PAGES; page++) {
    const params = new URLSearchParams({ limit: FORM_SUBMISSIONS_PAGE_SIZE.toString() });
    if (after) params.set("after", after);

    const result = await safeFetch(
      `${HUBSPOT_API_BASE}/form-integrations/v1/submissions/forms/${encodeURIComponent(formGuid)}?${params.toString()}`,
      { headers: buildHubSpotHeaders(accessToken) },
    );

    if (!result.success) {
      if (result.error.providerStatus === 403) {
        return adapterOk({ kind: "scope_missing" as const });
      }
      if (result.error.providerStatus === 404) {
        return adapterOk({ kind: "ok" as const, data: { count: 0, truncated: false } });
      }
      return adapterErr(result.error);
    }

    let body: { results?: unknown[]; paging?: { next?: { after?: string } } };
    try {
      body = (await result.data.json()) as typeof body;
    } catch {
      return adapterErr({
        code: "invalid_response",
        message: "HubSpot form submissions returned invalid JSON",
        retryable: false,
      });
    }

    const results = Array.isArray(body.results) ? body.results : [];
    count += results.length;

    const nextCursor = body.paging?.next?.after;
    if (!nextCursor) {
      // Exhausted the form's submissions within our page budget — accurate total.
      return adapterOk({ kind: "ok" as const, data: { count, truncated: false } });
    }
    after = nextCursor;
  }

  // R2-M1: Exited the loop with `after` still set → hit MAX_FORM_SUBMISSION_PAGES
  // with more pages remaining. Returned count is a floor, not the true total.
  return adapterOk({ kind: "ok" as const, data: { count, truncated: true } });
}

/**
 * Story 25.10: Marketing Event object returned by /marketing/v3/marketing-events.
 *
 * Counters (`registrants`, `attendees`, `cancellations`, `noShows`) are embedded
 * on each event record — no second API call required per event.
 *
 * `eventStatus` reflects lifecycle (`CANCELLED`, `COMPLETED`, etc). Events without
 * an explicit status fall back to `eventCompleted`/`eventCancelled` booleans.
 */
export interface HubSpotMarketingEvent {
  readonly objectId?: string | null;
  readonly externalEventId?: string | null;
  readonly eventName?: string | null;
  readonly eventType?: string | null;
  readonly startDateTime?: string | null;
  readonly endDateTime?: string | null;
  readonly eventStatus?: string | null;
  readonly eventCompleted?: boolean | null;
  readonly eventCancelled?: boolean | null;
  readonly registrants?: number | null;
  readonly attendees?: number | null;
  readonly cancellations?: number | null;
  readonly noShows?: number | null;
}

/**
 * Story 25.10: Fetch list of marketing events with embedded participation counters.
 * GET /marketing/v3/marketing-events?limit=100&after={cursor}
 *
 * Requires `crm.objects.marketing_events.read` optional scope. Returns
 * `scope_missing` on 403. Caps at MAX_MARKETING_EVENTS to prevent memory bloat.
 */
export async function fetchMarketingEvents(
  accessToken: string,
): Promise<AdapterResult<FormsEventsScopeResult<{ events: HubSpotMarketingEvent[]; truncated: boolean }>>> {
  const allResults: HubSpotMarketingEvent[] = [];
  let after: string | undefined;

  for (let page = 0; page < MAX_PAGES; page++) {
    const params = new URLSearchParams({ limit: MARKETING_EVENTS_PAGE_SIZE.toString() });
    if (after) params.set("after", after);

    const result = await safeFetch(
      `${HUBSPOT_API_BASE}/marketing/v3/marketing-events?${params.toString()}`,
      { headers: buildHubSpotHeaders(accessToken) },
    );

    if (!result.success) {
      if (result.error.providerStatus === 403) {
        return adapterOk({ kind: "scope_missing" as const });
      }
      return adapterErr(result.error);
    }

    let body: HubSpotPaginated<HubSpotMarketingEvent>;
    try {
      body = (await result.data.json()) as HubSpotPaginated<HubSpotMarketingEvent>;
    } catch {
      return adapterErr({
        code: "invalid_response",
        message: "HubSpot marketing events returned invalid JSON",
        retryable: false,
      });
    }

    const results = Array.isArray(body.results) ? body.results : [];
    allResults.push(...results);
    const nextCursor = body.paging?.next?.after;

    if (allResults.length >= MAX_MARKETING_EVENTS) {
      const truncated = allResults.length > MAX_MARKETING_EVENTS || Boolean(nextCursor);
      return adapterOk({
        kind: "ok" as const,
        data: { events: allResults.slice(0, MAX_MARKETING_EVENTS), truncated },
      });
    }

    if (!nextCursor) {
      return adapterOk({ kind: "ok" as const, data: { events: allResults, truncated: false } });
    }
    after = nextCursor;
  }

  return adapterOk({ kind: "ok" as const, data: { events: allResults, truncated: true } });
}

// ============================================================================
// Story 25.11: Workflows (Automation v4 BETA) + Custom Behavioral Events
//
// Workflows: /automation/v4/flows lists workflows but does NOT embed enrollment
// counters — per-flow details are on /automation/v4/flows/{flowId}. Automation
// v4 is a BETA API; responses change without notice, so we wrap every call in
// try/catch and degrade gracefully when the adapter hits unexpected shapes.
//
// Custom events: /events/v3/event-definitions lists portal-configured event
// types. For each event type we then call /events/v3/events?eventType=... to
// count occurrences inside the requested date window. Most portals have zero
// custom events, so we short-circuit the whole section when the definitions
// list is empty.
//
// Both endpoint groups use optional scopes (`automation`,
// `behavioral_events.event_definitions.read_write`); 403 → `scope_missing` so
// the adapter flags unavailable rather than failing the whole fetch.
// ============================================================================

/** Workflows scope-aware result (same shape as EmailStatsScopeResult for reuse). */
export type WorkflowsScopeResult<T> = EmailStatsScopeResult<T>;

/** Custom events scope-aware result (same shape). */
export type CustomEventsScopeResult<T> = EmailStatsScopeResult<T>;

/** Maximum workflows iterated per fetch cycle. 1 list call + 30 details calls
 * (worst case) = 31 total calls — well inside our 100 req/10s budget. */
export const MAX_WORKFLOWS = 30;

/** Page size for /automation/v4/flows list. Automation v4 returns up to 100. */
const WORKFLOWS_PAGE_SIZE = 100;

/** Maximum custom event types iterated per fetch cycle. 1 list call + 20
 * per-type event pages (MAX_PAGES*100 events each worst case) keeps us under
 * the rate-limit budget even for portals with many event definitions. */
export const MAX_EVENT_TYPES = 20;

/** Page size for /events/v3/events (cap is 100 for the v3 event analytics API). */
const CUSTOM_EVENTS_PAGE_SIZE = 100;

/** Page size for /events/v3/event-definitions (cap is 500). */
const EVENT_DEFINITIONS_PAGE_SIZE = 500;

/** Concurrent batch size for per-workflow details + per-event-type counts.
 * Mirrors campaign batching so all Story 25.* endpoint groups share rate
 * limits. Exported so tests can stub `setTimeout` by zeroing the delay. */
export const WORKFLOW_BATCH_SIZE = 5;
export const WORKFLOW_BATCH_DELAY_MS = 1200;

export const EVENT_TYPE_BATCH_SIZE = 5;
export const EVENT_TYPE_BATCH_DELAY_MS = 1200;

/** ID regex for workflow IDs — Automation v4 returns numeric flowIds as strings
 * (e.g., "123456"). Block path traversal when interpolating into the path. */
const WORKFLOW_ID_REGEX = /^[0-9]+$/;

/** Event-type internal-name regex per HubSpot rules: must start with a letter,
 * contain only lowercase a-z, 0-9, and underscores. Length 1-100 chars. */
const EVENT_TYPE_NAME_REGEX = /^[a-z][a-z0-9_]{0,99}$/;

/**
 * Story 25.11: Workflow metadata returned by /automation/v4/flows list.
 * List-endpoint only includes summary fields; detailed enrollment counters
 * require /automation/v4/flows/{flowId} follow-up calls.
 */
export interface HubSpotWorkflow {
  readonly id: string;
  readonly name?: string | null;
  readonly isEnabled?: boolean | null;
  readonly objectTypeId?: string | null;
  readonly revisionId?: string | null;
  readonly createdAt?: string | null;
  readonly updatedAt?: string | null;
}

/**
 * Story 25.11: Workflow enrollment counters derived from the details endpoint
 * response. Automation v4 BETA response has no stable schema for enrollment
 * stats; `fetchWorkflowDetails` extracts a shallow view with `total_enrollments`,
 * `active_enrollments`, `completed_enrollments`, and `avg_time_in_workflow_ms`
 * fields and surfaces `null` when the vendor did not emit a key.
 */
export interface HubSpotWorkflowStats {
  readonly total_enrollments: number | null;
  readonly active_enrollments: number | null;
  readonly completed_enrollments: number | null;
  readonly avg_time_in_workflow_ms: number | null;
}

/**
 * Story 25.11: Fetch list of workflows from Automation v4 BETA.
 * GET /automation/v4/flows?limit=100&after={cursor}
 *
 * Requires `automation` optional scope. Returns `scope_missing` on 403.
 * Caps at MAX_WORKFLOWS to protect the per-workflow details-call budget.
 *
 * BETA STABILITY: The vendor may change the response shape without notice.
 * We read only `results[].id` + friendly fields — anything else is ignored.
 */
export async function fetchWorkflows(
  accessToken: string,
): Promise<AdapterResult<WorkflowsScopeResult<{ workflows: HubSpotWorkflow[]; truncated: boolean }>>> {
  const allResults: HubSpotWorkflow[] = [];
  let after: string | undefined;

  for (let page = 0; page < MAX_PAGES; page++) {
    const params = new URLSearchParams({ limit: WORKFLOWS_PAGE_SIZE.toString() });
    if (after) params.set("after", after);

    const result = await safeFetch(
      `${HUBSPOT_API_BASE}/automation/v4/flows?${params.toString()}`,
      { headers: buildHubSpotHeaders(accessToken) },
    );

    if (!result.success) {
      if (result.error.providerStatus === 403) {
        return adapterOk({ kind: "scope_missing" as const });
      }
      return adapterErr(result.error);
    }

    let body: HubSpotPaginated<HubSpotWorkflow>;
    try {
      body = (await result.data.json()) as HubSpotPaginated<HubSpotWorkflow>;
    } catch {
      return adapterErr({
        code: "invalid_response",
        message: "HubSpot workflows list returned invalid JSON",
        retryable: false,
      });
    }

    const results = Array.isArray(body.results) ? body.results : [];
    allResults.push(...results);
    const nextCursor = body.paging?.next?.after;

    if (allResults.length >= MAX_WORKFLOWS) {
      const truncated = allResults.length > MAX_WORKFLOWS || Boolean(nextCursor);
      return adapterOk({
        kind: "ok" as const,
        data: { workflows: allResults.slice(0, MAX_WORKFLOWS), truncated },
      });
    }

    if (!nextCursor) {
      return adapterOk({ kind: "ok" as const, data: { workflows: allResults, truncated: false } });
    }
    after = nextCursor;
  }

  return adapterOk({ kind: "ok" as const, data: { workflows: allResults, truncated: true } });
}

/**
 * Story 25.11: Fetch per-workflow details including enrollment counters.
 * GET /automation/v4/flows/{flowId}
 *
 * Automation v4 BETA. The response schema is not fully documented; we extract
 * enrollment counts defensively from the common shapes we've observed:
 *   - `enrollmentCounters`, `stats`, or a top-level `summary` object
 *   - field aliases: `total`, `totalEnrollments`, `active`, `completed`, etc.
 * Missing fields surface as `null` so callers can disclaim partial data.
 *
 * Returns `scope_missing` on 403; `null` data on 404 (flow deleted between
 * list + details fetch).
 */
export async function fetchWorkflowDetails(
  accessToken: string,
  flowId: string,
): Promise<AdapterResult<WorkflowsScopeResult<HubSpotWorkflowStats | null>>> {
  if (!WORKFLOW_ID_REGEX.test(flowId)) {
    return adapterErr({
      code: "invalid_response",
      message: "Invalid HubSpot workflow ID format",
      retryable: false,
    });
  }

  const result = await safeFetch(
    `${HUBSPOT_API_BASE}/automation/v4/flows/${encodeURIComponent(flowId)}`,
    { headers: buildHubSpotHeaders(accessToken) },
  );

  if (!result.success) {
    if (result.error.providerStatus === 403) {
      return adapterOk({ kind: "scope_missing" as const });
    }
    if (result.error.providerStatus === 404) {
      return adapterOk({ kind: "ok" as const, data: null });
    }
    return adapterErr(result.error);
  }

  let body: Record<string, unknown>;
  try {
    body = (await result.data.json()) as Record<string, unknown>;
  } catch {
    // BETA API — invalid JSON is treated as "no stats available" rather than a
    // hard error. Adapter surfaces nulls and flags the workflow unavailable.
    return adapterOk({ kind: "ok" as const, data: null });
  }

  return adapterOk({ kind: "ok" as const, data: extractWorkflowStats(body) });
}

/**
 * Extract enrollment counters from an Automation v4 details response.
 * Defensive — the BETA API may move fields around between releases. We try
 * several known key paths and surface `null` when the value is missing.
 *
 * R2-M1: When all candidate paths return null AND the body wasn't empty, log
 * a `console.warn` telemetry line. This lets an SRE spot vendor schema drift
 * (e.g., HubSpot renames `enrollmentCounters` to `metrics`) without needing
 * each adapter cycle to error. The log stays silent for truly empty bodies
 * (BETA API returning `{}` for new flows) to avoid noise.
 *
 * Exported for unit test coverage; not re-exported from index.ts.
 */
export function extractWorkflowStats(body: unknown): HubSpotWorkflowStats {
  const obj = body && typeof body === "object" ? (body as Record<string, unknown>) : {};
  const stats = (obj.stats && typeof obj.stats === "object" ? (obj.stats as Record<string, unknown>) : {});
  const counters = (obj.enrollmentCounters && typeof obj.enrollmentCounters === "object"
    ? (obj.enrollmentCounters as Record<string, unknown>)
    : {});
  const summary = (obj.summary && typeof obj.summary === "object" ? (obj.summary as Record<string, unknown>) : {});

  const pickInt = (...candidates: unknown[]): number | null => {
    for (const v of candidates) {
      if (typeof v === "number" && isFinite(v) && v >= 0) return Math.round(v);
      if (typeof v === "string") {
        const n = Number(v);
        if (isFinite(n) && n >= 0) return Math.round(n);
      }
    }
    return null;
  };

  const total = pickInt(
    counters.total,
    counters.totalEnrollments,
    stats.total,
    stats.totalEnrollments,
    summary.total,
    summary.totalEnrollments,
    obj.totalEnrollments,
  );
  const active = pickInt(
    counters.active,
    counters.activeEnrollments,
    stats.active,
    stats.activeEnrollments,
    summary.active,
    summary.activeEnrollments,
    obj.activeEnrollments,
  );
  const completed = pickInt(
    counters.completed,
    counters.completedEnrollments,
    stats.completed,
    stats.completedEnrollments,
    summary.completed,
    summary.completedEnrollments,
    obj.completedEnrollments,
  );
  const avgTime = pickInt(
    counters.avgTimeInWorkflow,
    counters.avgTimeInWorkflowMs,
    stats.avgTimeInWorkflow,
    stats.avgTimeInWorkflowMs,
    summary.avgTimeInWorkflow,
    summary.avgTimeInWorkflowMs,
    obj.avgTimeInWorkflow,
  );

  // R2-M1: Telemetry — detect vendor schema drift. When the body has data but
  // no path yielded a value, warn once per call. Empty bodies (new flow, no
  // enrollments yet) stay silent.
  const allNull =
    total === null && active === null && completed === null && avgTime === null;
  const hasBodyContent = Object.keys(obj).length > 0;
  if (allNull && hasBodyContent) {
    console.warn(
      JSON.stringify({
        event: "hubspot.workflow_stats.schema_drift",
        message: "Automation v4 flow details returned data but no known enrollment key path matched.",
        bodyKeys: Object.keys(obj),
      }),
    );
  }

  return {
    total_enrollments: total,
    active_enrollments: active,
    completed_enrollments: completed,
    avg_time_in_workflow_ms: avgTime,
  };
}

/**
 * Story 25.11: Custom event definition returned by /events/v3/event-definitions.
 * `name` is the internal lowercase name (e.g., `pe12345_signup_started`) used
 * as the `eventType` query parameter when reading occurrences.
 */
export interface CustomEventDefinition {
  readonly name: string;
  readonly fullyQualifiedName?: string | null;
  readonly label?: string | null;
  readonly createdAt?: string | null;
}

/**
 * Story 25.11: List custom behavioral event definitions.
 * GET /events/v3/event-definitions?limit=500
 *
 * Requires `behavioral_events.event_definitions.read_write` optional scope.
 * Returns `scope_missing` on 403; empty list is the common case (most portals
 * have zero custom events configured).
 *
 * R3-M4: Single call with limit=500. HubSpot's hard-cap for this endpoint is
 * 500 per page. Portals with >500 custom event definitions are extremely rare
 * (HubSpot's own docs note most accounts have <50 event types). When we hit
 * exactly 500 we log the possibility of truncation — the adapter's
 * MAX_EVENT_TYPES=20 slice downstream already caps actual iteration, so a
 * portal with 500+ types would already be working with the first 20.
 */
export async function fetchEventDefinitions(
  accessToken: string,
): Promise<AdapterResult<CustomEventsScopeResult<CustomEventDefinition[]>>> {
  const params = new URLSearchParams({ limit: EVENT_DEFINITIONS_PAGE_SIZE.toString() });

  const result = await safeFetch(
    `${HUBSPOT_API_BASE}/events/v3/event-definitions?${params.toString()}`,
    { headers: buildHubSpotHeaders(accessToken) },
  );

  if (!result.success) {
    if (result.error.providerStatus === 403) {
      return adapterOk({ kind: "scope_missing" as const });
    }
    return adapterErr(result.error);
  }

  let body: { results?: CustomEventDefinition[]; paging?: { next?: { after?: string } } };
  try {
    body = (await result.data.json()) as typeof body;
  } catch {
    return adapterErr({
      code: "invalid_response",
      message: "HubSpot event definitions returned invalid JSON",
      retryable: false,
    });
  }

  const results = Array.isArray(body.results) ? body.results : [];
  // Filter out malformed entries defensively — a definition without a `name`
  // cannot be used as an eventType query filter.
  const clean = results.filter(
    (d): d is CustomEventDefinition => typeof d?.name === "string" && d.name.length > 0,
  );

  // R3-M4: Defensive telemetry when pagination appears but we don't fetch it.
  // MAX_EVENT_TYPES downstream slice caps work anyway; this warn helps an SRE
  // notice portals that would benefit from pagination support later.
  if (body.paging?.next?.after) {
    console.warn(
      JSON.stringify({
        event: "hubspot.event_definitions.pagination_skipped",
        message: "/events/v3/event-definitions returned next-page cursor; adapter uses first page only.",
        firstPageCount: clean.length,
      }),
    );
  }

  return adapterOk({ kind: "ok" as const, data: clean });
}

/**
 * Story 25.11: Count occurrences of a single custom event type + capture the
 * latest `occurredAt` timestamp. Uses pagination with `after` cursor; capped
 * at MAX_PAGES * CUSTOM_EVENTS_PAGE_SIZE events per type.
 *
 * Path: GET /events/v3/events?eventType=X&occurredAfter=Y&occurredBefore=Z
 * Returns `scope_missing` on 403; partial count with `truncated=true` when we
 * exhaust MAX_PAGES.
 */
export async function fetchCustomEventCount(
  accessToken: string,
  eventType: string,
  startDate: Date,
  endDate: Date,
): Promise<
  AdapterResult<
    CustomEventsScopeResult<{ count: number; latestOccurredAt: string | null; truncated: boolean }>
  >
> {
  if (!EVENT_TYPE_NAME_REGEX.test(eventType)) {
    return adapterErr({
      code: "invalid_response",
      message: "Invalid HubSpot event type internal name",
      retryable: false,
    });
  }

  let count = 0;
  let latestOccurredAt: string | null = null;
  let after: string | undefined;

  for (let page = 0; page < MAX_PAGES; page++) {
    const params = new URLSearchParams({
      eventType,
      occurredAfter: startDate.toISOString(),
      occurredBefore: endDate.toISOString(),
      limit: CUSTOM_EVENTS_PAGE_SIZE.toString(),
    });
    if (after) params.set("after", after);

    const result = await safeFetch(
      `${HUBSPOT_API_BASE}/events/v3/events?${params.toString()}`,
      { headers: buildHubSpotHeaders(accessToken) },
    );

    if (!result.success) {
      if (result.error.providerStatus === 403) {
        return adapterOk({ kind: "scope_missing" as const });
      }
      return adapterErr(result.error);
    }

    let body: {
      results?: Array<{ occurredAt?: string | null }>;
      paging?: { next?: { after?: string } };
    };
    try {
      body = (await result.data.json()) as typeof body;
    } catch {
      return adapterErr({
        code: "invalid_response",
        message: "HubSpot custom events returned invalid JSON",
        retryable: false,
      });
    }

    const results = Array.isArray(body.results) ? body.results : [];
    count += results.length;

    // Track the single most-recent occurredAt across all pages.
    for (const row of results) {
      const ts = row?.occurredAt;
      if (typeof ts === "string" && ts.length > 0) {
        if (latestOccurredAt === null || ts > latestOccurredAt) {
          latestOccurredAt = ts;
        }
      }
    }

    const nextCursor = body.paging?.next?.after;
    if (!nextCursor) {
      return adapterOk({
        kind: "ok" as const,
        data: { count, latestOccurredAt, truncated: false },
      });
    }
    after = nextCursor;
  }

  return adapterOk({
    kind: "ok" as const,
    data: { count, latestOccurredAt, truncated: true },
  });
}
